import { Component, OnInit, signal, computed, HostListener } from '@angular/core';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MENU_ITEMS } from 'src/app/helper/class/page-menu';
import {
  UrlServices,
  urlPermission,
  urlSegment,
} from 'src/app/helper/class/url-services';
import {
  cloneData,
  dateInstance,
  getFinancialYear,
  isArray,
  isEmptyObj,
} from 'src/app/helper/class/utilityHelper';
import { linkOption, menu } from 'src/app/helper/interface/menu-interface';
import {
  formBuilderData,
  ResponseData,
  urlConfig,
} from 'src/app/helper/interface/response';
import { User, userPermission } from 'src/app/helper/interface/user';
import { AuthService } from 'src/app/helper/service/auth.service';
import { ModalService } from 'src/app/shared/service/modal.service';
import { environment } from 'src/environments/environment';
import { NavigationService } from '../../service/navigation.service';
import { ChangePasswordComponent } from '../change-password/change-password.component';
import { ChatAiComponent } from 'src/app/shared/component/chat-ai/chat-ai.component';
import { SettingsService } from 'src/app/helper/service/settings.service';
import { MasterApiService } from 'src/app/modules/master/service/master-api.service';
import { ImageUploadModule } from 'src/app/shared/shared.module';
import { FormHelperModule } from 'src/app/shared/form/form-hepler.module';

import { TranslatePipe } from 'src/app/helper/pipe/translate.pipe';
import { TranslationService } from 'src/app/helper/service/translation.service';

@Component({
  selector: 'app-user-header',
  templateUrl: './user-header.component.html',
  styleUrls: ['./user-header.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ImageUploadModule,
    FormHelperModule,
    TitleCasePipe,
    TranslatePipe
  ]
})
export class UserHeaderComponent implements OnInit {
  menu = signal<any[]>([]);
  isDev = !environment.production;
  logo = signal('');
  title = signal('');
  urlService = UrlServices.PAGE_URL;
  user = computed(() => this.auth.user());
  roleName = computed(() => this.user()?.role?.[0]?.roleName || 'Staff');
  emailListPermission = signal(false);
  activeSubmenu = signal<string | null>(null);
  themeMode = signal<'light' | 'dark' | 'system'>('light');
  currentLanguage = signal<'en' | 'ta' | 'hi'>('en');
  fontSize = signal<'sm' | 'md' | 'lg'>('md');
  marqueeItems = signal<any[]>([]);
  yearOfExpPayroll: number | null = null;
  formDate: formBuilderData[] = [
    {
      colName: 'date_of_join',
      defaultValue: new Date(),
      type: 'DATE',
      validator: [{ name: 'required' }],
    },
    {
      colName: 'fin_year',
      title: 'Start Fin Year',
      type: 'DATE',
      validator: [{ name: 'required' }],
      defaultValue: getFinancialYear(),
    },
  ];
  constructor(
    private auth: AuthService,
    private settingsService: SettingsService,
    private modalService: ModalService,
    private navServe: NavigationService,
    private masterApi: MasterApiService,
    private translationService: TranslationService,
  ) {}

  ngOnInit(): void {
    this.currentLanguage.set(this.translationService.getLanguage());
    this.fetchMarqueeData();
    this.setUserMenus();
    this.logo.set(this.settingsService.snapshot?.siteLogo || '');
    this.title.set(this.settingsService.snapshot?.siteName || '');
    this.getTemplateModule();
    if (+(this.user()?.staff_fk_id || 0) == 0) {
      this.emailListPermission.set(true);
    }
    this.initTheme();
    this.initLanguage();
    this.initFontSize();
    this.fetchMarqueeData();
    // Auto-refresh marquee every 5 minutes
    setInterval(() => this.fetchMarqueeData(), 300000);
  }

  fetchMarqueeData() {
    this.masterApi.http.get('marquee', { 
      headers: { 'Accept-Language': this.currentLanguage() } 
    }).subscribe((res: any) => {
      if (res?.statusCode == 200) {
        this.marqueeItems.set(res.result || []);
      }
    });
  }

  initLanguage() {
    const savedLang = localStorage.getItem('app-lang') as 'en' | 'ta' | 'hi' || 'en';
    this.currentLanguage.set(savedLang);
  }

  setLanguage(lang: 'en' | 'ta' | 'hi') {
    this.currentLanguage.set(lang);
    this.translationService.setLanguage(lang);
    localStorage.setItem('app-lang', lang);
    this.fetchMarqueeData(); // Refresh marquee with new language
  }

  initTheme() {
    const savedTheme = localStorage.getItem('theme-mode') as 'light' | 'dark' | 'system' || 'light';
    this.setTheme(savedTheme);
  }

  setTheme(mode: 'light' | 'dark' | 'system') {
    this.themeMode.set(mode);
    localStorage.setItem('theme-mode', mode);

    const body = document.body;
    body.classList.remove('light_version', 'dark_version');

    if (mode === 'system') {
      const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      body.classList.add(isDark ? 'dark_version' : 'light_version');
    } else {
      body.classList.add(mode === 'dark' ? 'dark_version' : 'light_version');
    }
  }

  initFontSize() {
    const savedSize = localStorage.getItem('font-size-mode') as 'sm' | 'md' | 'lg' || 'md';
    this.setFontSize(savedSize);
  }

  setFontSize(size: 'sm' | 'md' | 'lg') {
    this.fontSize.set(size);
    localStorage.setItem('font-size-mode', size);

    const html = document.documentElement;
    html.classList.remove('font-sm', 'font-md', 'font-lg');
    html.classList.add(`font-${size}`);
  }
  get payrollPermission() {
    return this.auth.checkPermission('PAYROLL', [
      'ADD',
      'PROCESS',
      'VERIFY',
      'APPROVE',
    ]);
  }
  getTime() {
    const date = new Date(),
      hours = date.getHours(),
      ampm = hours >= 12 ? 'PM' : 'AM',
      hours_d = hours % 12 ? hours % 12 : 12,
      min = (date.getMinutes() < 10 ? '0' : '') + date.getMinutes();
    return `${date?.toLocaleDateString('locale', {
      month: 'short',
      day: 'numeric',
    })} ${hours_d}:${min} ${ampm}`;
  }
  checkPermission(item: menu): urlConfig {
    const linkA = isArray(item.link)
      ? item.link
      : !isEmptyObj(item.link)
        ? [item.link]
        : ([{}] as any);
    const urls: urlConfig[] = [];
    linkA.forEach((link: linkOption) => {
      // const link: linkOption = l?.link || {} as any;
      let url = link.url || ({} as urlConfig),
        per: userPermission = {} as any;
      if (!isEmptyObj(link) && !isEmptyObj(link.url)) {
        if (link.permission == undefined) {
          // check assign permission
          per = urlPermission(link.url.keyName, link.url.subKeyName);
        } else if (isEmptyObj(link.permission)) {
          urls.push(link.url);
          return link.url;
        } else {
          per = link.permission;
        }
        if (!isEmptyObj(per)) {
          const res = per.actionName
            ? this.auth.checkPermission(per.moduleName, per.actionName)
            : true;
          if (!res) {
            //checkQuery Param match any permission
            if (item.queryParams) {
              //@ts-ignore
              const qp = this.navServe.getParamByPermission(
                item.queryParams,
                per.moduleName,
              );
              if (!isEmptyObj(qp)) {
                link.url.queryparam = {};
                link.url.queryparam = qp;
                urls.push(link.url);
                return link.url;
              } else {
                return urls.push({} as urlConfig);
              }
            } else {
              urls.push({} as urlConfig);
              return {} as urlConfig;
            }
          }
        }
      } else if (isEmptyObj(link.url)) {
        urls.push({} as urlConfig);
      }
      //check permission for queryParams
      urls.push(url);
      return url;
    });
    return urls.filter((a) => !isEmptyObj(a))[0] || ({} as urlConfig);
  }

  getTemplateModule() {
    this.masterApi.getExportModule().then((res: ResponseData) => {
      if (isArray(res.result)) {
        this.menu.update(prev => [...prev, {
          title: 'Report',
          icon: 'icon-speedometer',
          data: { permission: {} as userPermission },
          urlString : urlSegment('REPORT', 'STAFF_FLAT_REPORT')
        }]);
      }
    });
  }

  setUserMenus() {
    const menuItems: menu[] = [] as menu[];
    cloneData(MENU_ITEMS).forEach((item: menu, index) => {
      if (item.children?.length) {
        const chMenu: any = [];
        item.children.map((a: any) => {
          const chk: urlConfig | any = this.checkPermission(a);
          if (!isEmptyObj(chk) && chk.keyName) {
            chk.urlString = urlSegment(chk.keyName, chk.subKeyName);
            chk.title = a.title;
            chMenu.push(chk);
          }
        });
        if (!chMenu.length) {
          return;
        }
        item.children = chMenu;
      } else {
        const pM: urlConfig = this.checkPermission(item);
        if (isEmptyObj(pM)) {
          return;
        } else {
          //@ts-ignore
          item.urlString = urlSegment(pM.keyName, pM.subKeyName);
        }
      }
      menuItems.push(item);
    });
    this.menu.set(menuItems);
  }

  transformUrl(u: urlConfig) {
    if (!u.urlString) {
      u.urlString = urlSegment(u.keyName, u.subKeyName);
    }
    return u;
  }
  openChangePassword() {
    this.modalService
      .openModal(ChangePasswordComponent, { mode: 'CHANGE' }, 'modal-md')
      .then((res) => {
        if (res) {
          this.auth.logout();
        }
      });
  }

  logout() {
    this.auth.logout();
  }
  profilePage() {
    this.closeSubmenu();
    this.navServe.navigatePageSegment('STAFF','VIEW',{id:'my_profile'});
  }
   emailLogPage() {
    this.closeSubmenu();
    this.navServe.navigatePageSegment('EMAIL','LIST');
  }
  policyPage(){
    this.closeSubmenu();
    this.navServe.navigatePageSegment('MASTER','DOCUMENT_VIEW');
  }
  modalRef: any;
  async calSI(temp: any) {
    this.modalRef = await this.modalService?.openModalTemplate(temp);
  }

  calculateYearOfExpPayroll(payload: any): void {
    const now = new Date();
    const joinDate = dateInstance(payload.date_of_join ?? now);
    const finStart = getFinancialYear(payload?.fin_year ?? new Date());
    let diff = finStart.getFullYear() - joinDate.getFullYear();
    if (
      finStart.getMonth() < joinDate.getMonth() ||
      (finStart.getMonth() === joinDate.getMonth() &&
        finStart.getDate() < joinDate.getDate())
    ) {
      diff--;
    }
    this.yearOfExpPayroll = Math.abs(diff);
  }
  close(temp) {
    this.modalRef.hide();
  }
  openAi() {
    this.modalService.openModal(ChatAiComponent, {}, 'modal-lg');
  }

  toggleSubmenu(event: Event, title: string) {
    event.preventDefault();
    event.stopPropagation();
    if (this.activeSubmenu() === title) {
      this.activeSubmenu.set(null);
    } else {
      this.activeSubmenu.set(title);
    }
  }

  @HostListener('document:click')
  closeSubmenu() {
    if (this.activeSubmenu()) {
      this.activeSubmenu.set(null);
    }
  }

  openNoticeDetails(item: any) {
    if (item.id) {
      this.masterApi.getNoticeById(item.id).then((res: any) => {
        if (res.statusCode == 200) {
          const detail = res.result;
          let content = detail.content || '';
          if (detail.attachment_path) {
            content += `<div class="mt-4 border-top pt-3">
                          <h6 class="mb-2">Attachment</h6>
                          <a href="${detail.attachment_path}" target="_blank" class="btn btn-sm btn-primary">
                              <i class="pi pi-download mr-1"></i> Download Attachment
                          </a>
                        </div>`;
          }
          this.modalService.openInfoModal({
            title: detail.title,
            content: content,
            btn: 'Close'
          }, 'modal-lg');
        }
      }).catch(err => {
        console.error('Error fetching notice details:', err);
      });
    }
  }

  trackByTitle(index: number, item: any): string {
    return item?.title || index;
  }
}
